/**
 * A coluna lateral de decisão da mesa (E78.F4).
 *
 * Fica fixa ao lado do quadro: o perito lê o eixo divergente e decide sem
 * perder de vista nem o quadro nem a régua. Concentra também o que a página não
 * precisa saber — qual é a próxima peça a decidir e como avisar a decisão que
 * deu certo ou errado naquela peça.
 */
import { Blueprint } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ConclusaoEletronicaInput, ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { tituloDaPeca } from '@/lib/exame_eletronico_peca';

import { DecisaoDoPerito, type DecisaoAcao } from './DecisaoDoPerito';

const t = ptBR.mesaEletronica;

/**
 * A próxima peça a decidir depois da atual, dando a volta na lista.
 *
 * Dar a volta importa: quem abre a última peça e a conclui precisa ser levado
 * às que ficaram para trás, não a um botão morto.
 */
export function proximaADecidir(
  pecas: readonly ExameEletronicoCcbData[],
  atual: string | null,
): ExameEletronicoCcbData | null {
  const partida = Math.max(
    pecas.findIndex((p) => p.source_document_id === atual),
    0,
  );
  for (let passo = 1; passo <= pecas.length; passo += 1) {
    const candidata = pecas[(partida + passo) % pecas.length];
    if (candidata === undefined || candidata.source_document_id === atual) continue;
    if (candidata.exame?.status !== 'concluido') return candidata;
  }
  return null;
}

export interface ColunaDeDecisaoProps {
  pecas: readonly ExameEletronicoCcbData[];
  peca: ExameEletronicoCcbData;
  indice: number;
  acaoEmCurso: DecisaoAcao;
  erro: string | undefined;
  sucesso: string | undefined;
  onConcluir: (conclusao: ConclusaoEletronicaInput) => void;
  onReabrir: (motivo: string) => void;
  onSelecionar: (sourceDocumentId: string) => void;
}

export function ColunaDeDecisao({
  pecas,
  peca,
  indice,
  acaoEmCurso,
  erro,
  sucesso,
  onConcluir,
  onReabrir,
  onSelecionar,
}: ColunaDeDecisaoProps): JSX.Element {
  const docId = peca.source_document_id;
  const proxima = proximaADecidir(pecas, docId);
  const indiceProxima =
    proxima === null
      ? -1
      : pecas.findIndex((p) => p.source_document_id === proxima.source_document_id);

  return (
    <aside className="lg:sticky lg:top-sc-4 lg:self-start" data-testid="coluna-de-decisao">
      <Blueprint className="space-y-sc-3 p-sc-6">
        <h2 className="text-display-sm text-fg">
          {t.decisaoDaPeca.replace('{peca}', tituloDaPeca(peca, indice))}
        </h2>
        {erro !== undefined ? (
          <p className="text-body-sm text-danger-700" role="alert" data-testid="mesa-decisao-erro">
            {erro}
          </p>
        ) : null}
        {sucesso !== undefined ? (
          <p
            className="text-body-sm text-fg-muted"
            role="status"
            data-testid="mesa-decisao-sucesso"
          >
            {sucesso}
          </p>
        ) : null}
        <DecisaoDoPerito
          key={docId}
          ccb={peca}
          idPrefix={`mesa-${docId}`}
          acaoEmCurso={acaoEmCurso}
          proximaPeca={proxima === null ? null : tituloDaPeca(proxima, indiceProxima)}
          onConcluir={onConcluir}
          onReabrir={onReabrir}
          {...(proxima === null
            ? {}
            : { onProximaPeca: () => onSelecionar(proxima.source_document_id) })}
        />
      </Blueprint>
    </aside>
  );
}
